import { supabase } from '../lib/supabase';

/**
 * Service for user profile and statistics operations
 */
export class UserService {
  /**
   * Gets user profile with statistics
   * @param {string} userId - User ID
   * @returns {Promise<Object>} User profile with stats
   */
  static async getProfileWithStats(userId) {
    try {
      // Get user profile
      const { data: profile, error: profileError } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (profileError) throw profileError;

      // Get resume analyses count
      const { data: analyses, error: analysesError } = await supabase
        .from('resume_analyses')
        .select('id, overall_score, created_at')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (analysesError) {
        console.error('Error fetching analyses:', analysesError);
      }

      // Get skill gaps count
      const { data: skillGaps, error: skillGapsError } = await supabase
        .from('skill_gaps')
        .select('id')
        .eq('user_id', userId);

      if (skillGapsError) {
        console.error('Error fetching skill gaps:', skillGapsError);
      }

      // Calculate statistics
      const stats = {
        total_analyses: analyses?.length || 0,
        skill_gaps_count: skillGaps?.length || 0,
        average_score: analyses?.length > 0
          ? Math.round(analyses.reduce((sum, a) => sum + (a.overall_score || 0), 0) / analyses.length)
          : 0,
        progress_data: (Array.isArray(analyses) && analyses.length > 0)
          ? analyses.slice(0, 5).reverse().map((a, idx) => ({
            name: `Analysis #${idx + 1}`,
            score: Math.min(100, Math.max(0, a.overall_score || 0)), // Clamp between 0-100
            date: a.created_at
          }))
          : []
      };

      return {
        data: {
          ...profile,
          stats,
          skills: [] // Will be populated from skill_gaps if needed
        },
        error: null
      };
    } catch (error) {
      console.error('Error getting profile with stats:', error);
      return {
        data: null,
        error: error.message
      };
    }
  }

  /**
   * Updates user profile
   * @param {string} userId - User ID
   * @param {Object} updates - Fields to update
   * @returns {Promise<Object>} Update result
   */
  static async updateProfile(userId, updates) {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', userId)
        .select()
        .single();

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Error updating profile:', error);
      return { data: null, error: error.message };
    }
  }

  /**
   * Gets user's recent activity
   * @param {string} userId - User ID
   * @param {number} limit - Number of activities to return
   * @returns {Promise<Array>} Array of activities
   */
  static async getRecentActivity(userId, limit = 10) {
    try {
      const { data, error } = await supabase
        .from('resume_analyses')
        .select('id, file_name, overall_score, status, created_at')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) throw error;

      return { data: data || [], error: null };
    } catch (error) {
      console.error('Error getting recent activity:', error);
      return { data: [], error: error.message };
    }
  }
  static async getPaymentIterations(userId) {
    try {
      const { data, error } = await supabase
        .from('user_payment_iterations')
        .select('*')
        .eq('user_id', userId)
        .order('payment_iteration', { ascending: false });

      if (error) {
        // Handle missing table gracefully (table may not exist yet)
        if (error.code === '42P01' || error.message?.includes('does not exist')) {
          console.warn('⚠️ user_payment_iterations table does not exist yet. Run migrations.');
          return { data: [], error: null };
        }
        throw error;
      }
      return { data: error ? [] : data, error: null };
    } catch (err) {
      console.error('Error fetching payment history:', err);
      return { data: [], error: err.message };
    }
  }

}

export default UserService;